import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth";
import LogoutButton from "@/components/LogoutButton";
import { useAdmin } from "@/components/admin/AdminGate";

/**
 * UserMenu — authenticated-only badge in the TopNav CTA slot.
 *
 * Shows the user's first name (fallback: email). Clicking expands a small
 * menu containing the logout control. Keyboard + ARIA story: the trigger is
 * a button with `aria-expanded`/`aria-controls`; clicking outside or pressing
 * Escape closes the menu. No router links yet — this is the minimum surface
 * that lets E41.F2 call itself done.
 */
export default function UserMenu() {
  const { t } = useTranslation();
  const { state } = useAuth();
  const { isAdmin } = useAdmin();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(ev: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(ev.target as Node)) setOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      const firstItem = containerRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
      firstItem?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  if (state.kind !== "authenticated") return null;

  const { user } = state;
  const displayName = user.first_name?.trim() || user.email;

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-xs rounded-md border border-brand-primary/20 bg-white px-sm py-xs text-sm font-medium text-brand-primary transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        data-testid="user-menu-trigger"
      >
        <span className="sr-only">{t("auth.welcomeBack")}: </span>
        <span>{displayName}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          aria-hidden="true"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 mt-xs w-48 rounded-md border border-brand-primary/10 bg-white p-xs shadow-lg"
          data-testid="user-menu"
        >
          <div className="px-sm py-xs text-xs text-neutral-600">{user.email}</div>
          <div className="mt-xs border-t border-brand-primary/10 pt-xs">
            <Link
              to="/exam/CA"
              role="menuitem"
              onClick={() => setOpen(false)}
              data-testid="user-menu-exam"
              className="block w-full rounded-sm px-sm py-xs text-left text-sm font-medium text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("nav.exam")}
            </Link>
            <Link
              to="/practice"
              role="menuitem"
              onClick={() => setOpen(false)}
              data-testid="user-menu-practice"
              className="block w-full rounded-sm px-sm py-xs text-left text-sm font-medium text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("nav.practice")}
            </Link>
            <Link
              to="/dashboard"
              role="menuitem"
              onClick={() => setOpen(false)}
              data-testid="user-menu-dashboard"
              className="block w-full rounded-sm px-sm py-xs text-left text-sm font-medium text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("nav.dashboard")}
            </Link>
            {isAdmin && (
              <Link
                to="/admin/users"
                role="menuitem"
                onClick={() => setOpen(false)}
                data-testid="user-menu-admin"
                className="block w-full rounded-sm px-sm py-xs text-left text-sm font-medium text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {t("nav.admin")}
              </Link>
            )}
            <Link
              to="/settings"
              role="menuitem"
              onClick={() => setOpen(false)}
              data-testid="user-menu-settings"
              className="block w-full rounded-sm px-sm py-xs text-left text-sm font-medium text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("nav.settings")}
            </Link>
            <Link
              to="/rule-diff/preview"
              role="menuitem"
              onClick={() => setOpen(false)}
              data-testid="user-menu-rule-diff"
              className="block w-full rounded-sm px-sm py-xs text-left text-sm font-medium text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("nav.ruleDiffPreview")}
            </Link>
            <Link
              to="/rule-diff/browse"
              role="menuitem"
              onClick={() => setOpen(false)}
              data-testid="user-menu-rule-diff-browse"
              className="block w-full rounded-sm px-sm py-xs text-left text-sm font-medium text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("nav.ruleDiffBrowse")}
            </Link>
          </div>
          <div className="mt-xs border-t border-brand-primary/10 pt-xs">
            <LogoutButton
              className="block w-full rounded-sm px-sm py-xs text-left text-sm font-medium text-brand-primary transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            />
          </div>
        </div>
      )}
    </div>
  );
}
